<template>
  <div class="avatar-cropper">
    <div v-if="!image" class="upload-section">
      <FileUpload mode="basic"
                  chooseLabel="Profilbild auswählen"
                  accept="image/*"
                  :customUpload="true"
                  @uploader="onFileSelect"
                  :maxFileSize="50000000"/>
    </div>

    <div v-else class="crop-section">
      <div class="crop-container">
        <div class="crop-preview" ref="cropPreview">
          <img :src="image"
               :style="imageStyle"
               @mousedown="startDrag"
               @touchstart="startDrag"
               class="crop-image"/>
          <div class="crop-circle"></div>
        </div>

        <div class="zoom-controls">
          <Button icon="pi pi-minus" class="p-button-sm p-button-text" @click="decreaseZoom"/>
          <Slider v-model="zoom" :min="1" :max="3" :step="0.1" class="zoom-slider"/>
          <Button icon="pi pi-plus" class="p-button-sm p-button-text" @click="increaseZoom"/>
        </div>
      </div>

      <div class="action-buttons">
        <Button label="Bestätigen" icon="pi pi-check" @click="confirmCrop" class="p-button-success"/>
        <Button label="Abbrechen" icon="pi pi-times" @click="cancelCrop" class="p-button-secondary"/>
      </div>
    </div>
  </div>
</template>

<script>
import imageCompression from 'browser-image-compression';

export default {
  name: 'AvatarCropper',
  data() {
    return {
      image: null,
      zoom: 1,
      offsetX: 0,
      offsetY: 0,
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      startOffsetX: 0,
      startOffsetY: 0,
      croppedImage: null
    };
  },
  computed: {
    imageStyle() {
      return {
        transform: `scale(${this.zoom})`,
        left: `${this.offsetX}px`,
        top: `${this.offsetY}px`,
        cursor: this.isDragging ? 'grabbing' : 'grab'
      };
    }
  },
  methods: {
    async onFileSelect(event) {
      const file = event.files[0];

      try {
        // Komprimiere das Bild
        const options = {
          maxSizeMB: 0.5,
          maxWidthOrHeight: 800,
          useWebWorker: true
        };
        const compressedFile = await imageCompression(file, options);

        // Lese das Bild als Data URL
        const reader = new FileReader();
        reader.onload = (e) => {
          this.image = e.target.result;
          this.resetCropPosition();
        };
        reader.readAsDataURL(compressedFile);
      } catch (error) {
        console.error('Fehler beim Komprimieren des Bildes:', error);
        this.$toast.add({severity: 'error', summary: 'Fehler', detail: 'Bild konnte nicht verarbeitet werden', life: 3000});
      }
    },

    resetCropPosition() {
      this.zoom = 1;
      this.offsetX = 0;
      this.offsetY = 0;
    },

    startDrag(event) {
      if (event.type.includes('touch')) {
        const touch = event.touches[0];
        this.dragStartX = touch.clientX;
        this.dragStartY = touch.clientY;
      } else {
        this.dragStartX = event.clientX;
        this.dragStartY = event.clientY;
      }

      this.startOffsetX = this.offsetX;
      this.startOffsetY = this.offsetY;
      this.isDragging = true;

      document.addEventListener('mousemove', this.onDrag);
      document.addEventListener('mouseup', this.endDrag);
      document.addEventListener('touchmove', this.onDrag);
      document.addEventListener('touchend', this.endDrag);
    },

    onDrag: function(event) {
      if (!this.isDragging) return;

      let clientX, clientY;
      if (event.type.includes('touch')) {
        clientX = event.touches[0].clientX;
        clientY = event.touches[0].clientY;
      } else {
        clientX = event.clientX;
        clientY = event.clientY;
      }

      const deltaX = clientX - this.dragStartX;
      const deltaY = clientY - this.dragStartY;

      this.offsetX = this.startOffsetX + deltaX;
      this.offsetY = this.startOffsetY + deltaY;
    },

    endDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.endDrag);
      document.removeEventListener('touchmove', this.onDrag);
      document.removeEventListener('touchend', this.endDrag);
    },

    increaseZoom() {
      if (this.zoom < 3) {
        this.zoom = Math.min(3, this.zoom + 0.1);
      }
    },

    decreaseZoom() {
      if (this.zoom > 1) {
        this.zoom = Math.max(1, this.zoom - 0.1);
      }
    },

    confirmCrop() {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const size = 300; // Größe des runden Bildes

      canvas.width = size;
      canvas.height = size;

      // Zeichne einen Kreis als Maske
      ctx.beginPath();
      ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
      ctx.clip();

      // Lade das Bild
      const img = new Image();
      img.onload = () => {
        // Zeichne das Bild exakt wie in der Preview
        // Das Bild wird mit zoom vergrößert und um offsetX, offsetY verschoben
        const scaledWidth = img.width * this.zoom;
        const scaledHeight = img.height * this.zoom;

        // Zeichne das skalierte Bild an der Position (offsetX, offsetY)
        ctx.drawImage(img, this.offsetX, this.offsetY, scaledWidth, scaledHeight);

        canvas.toBlob((blob) => {
          this.croppedImage = blob;
          this.$emit('image-cropped', {
            blob: blob,
            dataUrl: canvas.toDataURL('image/jpeg', 0.95)
          });

          // Zurücksetzen
          this.image = null;
          this.resetCropPosition();
        }, 'image/jpeg', 0.95);
      };
      img.src = this.image;
    },

    cancelCrop() {
      this.image = null;
      this.resetCropPosition();
      this.$emit('crop-cancelled');
    }
  },
  beforeDestroy() {
    document.removeEventListener('mousemove', this.onDrag);
    document.removeEventListener('mouseup', this.endDrag);
    document.removeEventListener('touchmove', this.onDrag);
    document.removeEventListener('touchend', this.endDrag);
  }
};
</script>

<style scoped>
.avatar-cropper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.upload-section {
  display: flex;
  justify-content: center;
  padding: 1rem;
}

.crop-section {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.crop-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  align-items: center;
}

.crop-preview {
  position: relative;
  width: 300px;
  height: 300px;
  border: 2px solid #ccc;
  border-radius: 50%;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.crop-image {
  position: absolute;
  cursor: grab;
  transition: transform 0.2s ease-out;
}

.crop-circle {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 3px dashed #007bff;
  pointer-events: none;
}

.zoom-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  max-width: 300px;
}

.zoom-slider {
  flex: 1;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  justify-content: center;
}

@media (max-width: 768px) {
  .crop-preview {
    width: 250px;
    height: 250px;
  }
}
</style>


